// Button.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './Button.css';

// Icons
import Icon from 'svg/Icon';

/**
 * Button
 *
 * Renders a versatile button with optional icon and label content.
 *
 * @param {ReactNode} icon - The icon component to render inside the button.
 * @param {string} iconKey - The icon key to render via Icon (overrides icon).
 * @param {string} iconColor - The color of the icon (default is white).
 * @param {string|number} iconSize - The size of the icon (default is 20).
 * @param {string} label - The label to display on the button.
 * @param {string} labelColor - The color of the label (default is white).
 * @param {string|number} fontSize - The font size of the label (default is 14px).
 * @param {string|number} width - The width of the button (default is auto).
 * @param {boolean} enabled - Whether the button is enabled (default is true).
 * @param {Function} onClick - Called when the button is clicked.
 * @param {string} backgroundColor - Optional background color override.
 * @param {string} buttonColor - Optional background color alias.
 * @param {string} foregroundColor - Optional foreground color override.
 * @param {string} borderColor - Optional border color override.
 * @param {string|number} borderRadius - Border radius value (default is 8px).
 * @param {string|number} height - The height of the button (default is 50px).
 * @param {string|number} flex - Optional flex value for layout.
 * @param {string} hoverBackgroundColor - Optional hover background override.
 * @param {string} hoverForegroundColor - Optional hover foreground override.
 * @param {string|number} padding - Optional padding override.
 * @param {string} variant - Optional visual variant for shared themed surfaces.
 *
 * @returns {ReactNode} The rendered component.
 */
const Button = React.memo(({
    icon: IconComponent,
    iconKey,
    iconColor,
    iconSize = '16px',
    label,
    labelColor,
    title,
    fontSize = '12px',
    borderRadius = '9px',
    height = '36px',
    width = 'auto',
    enabled = true,
    onClick,
    flex,
    gap = '8px',
    backgroundColor,
    buttonColor,
    foregroundColor,
    borderColor,
    hoverBackgroundColor,
    hoverForegroundColor,
    padding,
    variant = 'default',
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // ----- STATE ----- //

    // Hover State
    const [hovered, setHovered] = useState(false);

    // ----- MEMOS ----- //

    // Indicates whether the button renders any icon content.
    const hasIcon = Boolean(iconKey || IconComponent);

    // Indicates whether the button renders text label content.
    const hasLabel = Boolean(label);

    // Indicates icon-only button mode.
    const iconOnly = hasIcon && !hasLabel;

    // Resolves default padding for icon-only, icon+label, and label-only variants.
    const resolvedPadding = useMemo(() => {
        if (padding !== undefined) return padding;
        if (iconOnly) return '10px 20px';
        if (hasIcon) return '10px 20px 10px 10px';
        return '10px 18px';
    }, [padding, iconOnly, hasIcon]);

    // ----- UTILITIES ----- //

    const isGlassVariant = variant === 'glass';

    const baseBackground = backgroundColor || buttonColor || (isGlassVariant ? 'var(--panel-bg-soft)' : theme.buttonBackgroundColor || theme.backgroundMuted);
    const baseBorder = borderColor || (isGlassVariant ? 'var(--panel-border)' : theme.status);

    // Resolves current background color based on enabled and hover state.
    const resolvedBackground = useMemo(() => {
        if (!enabled || !hovered) return baseBackground;
        if (hoverBackgroundColor) return hoverBackgroundColor;
        if (backgroundColor || buttonColor) return baseBackground;
        if (isGlassVariant) return 'var(--panel-bg)';
        return theme.buttonHoverBackgroundColor || theme.backgroundHover;
    }, [enabled, hovered, baseBackground, hoverBackgroundColor, backgroundColor, buttonColor, isGlassVariant, theme.buttonHoverBackgroundColor, theme.backgroundHover]);

    // Resolves current border color based on enabled and hover state.
    const resolvedBorder = useMemo(() => {
        if (!enabled || !hovered) return baseBorder;
        if (borderColor) return borderColor;
        if (isGlassVariant) return 'var(--color-highlight)';
        return theme.foregroundMuted;
    }, [enabled, hovered, baseBorder, borderColor, isGlassVariant, theme.foregroundMuted]);

    const baseIconColor = iconColor || foregroundColor || theme.buttonIconColor || theme.foreground;
    const hoverIconColor = hoverForegroundColor || theme.buttonHoverIconColor || theme.foreground;

    const baseLabelColor = labelColor || foregroundColor || theme.buttonLabelColor || theme.foreground;
    const hoverLabelColor = hoverForegroundColor || theme.buttonHoverLabelColor || theme.foreground;

    // ----- HANDLERS ----- //

    /**
     * Handles click interaction when the button is enabled.
     *
     * @param {MouseEvent} event - Native click event.
     */
    const handleClick = (event) => {
        if (!enabled || !onClick) return;
        onClick(event);
    };

    /**
     * Handles hover entry state.
     */
    const handleMouseEnter = () => {
        setHovered(true);
    };

    /**
     * Handles hover exit state.
     */
    const handleMouseLeave = () => {
        setHovered(false);
    };

    // ----- RENDER ----- //

    return (

        <div
            className={`button-container${isGlassVariant ? ' button-variant-glass' : ''}`}
            title={title}
            style={{
                backgroundColor: resolvedBackground,
                borderColor: resolvedBorder,
                borderRadius,
                boxShadow: isGlassVariant ? '0 12px 34px var(--panel-shadow-soft)' : undefined,
                cursor: enabled ? 'pointer' : 'not-allowed',
                flex,
                gap,
                height,
                //justifyContent: iconOnly ? 'center' : hasIcon ? 'center' : 'center',
                opacity: enabled ? 1 : 0.4,
                padding: resolvedPadding,
                transform: hovered && enabled ? 'scale(1.01)' : 'scale(1)',
                width,
            }}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >

            {/* ICON */}
            {iconKey ? (
                <div className="button-icon">
                    <Icon
                        color={hovered ? hoverIconColor : baseIconColor}
                        height={iconSize}
                        iconKey={iconKey}
                        width={iconSize}
                    />
                </div>
            ) : IconComponent ? (
                <div className="button-icon">
                    <IconComponent
                        color={hovered ? hoverIconColor : baseIconColor}
                        height={iconSize}
                        width={iconSize}
                    />
                </div>
            ) : null}

            {/* LABEL */}
            {label && (
                <div
                    className="button-label"
                    style={{
                        color: hovered ? hoverLabelColor : baseLabelColor,
                        fontSize,
                    }}
                >
                    {label}
                </div>
            )}
        </div>
    );
});

// Dev Tools
Button.displayName = 'Button';

export default Button;

